import { logger } from '../../utils/logger';
import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Link,
    Grid,
    InputAdornment,
    IconButton,
    Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRegister } from '../../types/user';
import { validateEmail, validatePassword } from '../../utils/validation';
import GoogleSignIn from '../GoogleSignIn';

// showGoogle=false when the parent already renders the Google button: GoogleSignIn uses a fixed element id,
// so a second instance on the same page never rendered (empty "Sign up with Google" block on /get-started).
export const Register: React.FC<{ showGoogle?: boolean }> = ({ showGoogle = true }) => {
    const navigate = useNavigate();
    const { register, googleLogin } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        email?: string;
        password?: string;
        confirmPassword?: string;
        full_name?: string;
    }>({});

    const [formData, setFormData] = useState<UserRegister>({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
    });

    const validateForm = (): boolean => {
        const errors: typeof validationErrors = {};

        // Email validation
        if (!formData.email) {
            errors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        // Password validation
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (!validatePassword(formData.password)) {
            errors.password = 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character';
        }

        // Confirm password validation
        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        // Full name validation
        if (!formData.full_name) {
            errors.full_name = 'Full name is required';
        } else if (formData.full_name.length < 2) {
            errors.full_name = 'Full name must be at least 2 characters long';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error when user starts typing
        if (validationErrors[name as keyof typeof validationErrors]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            await register(formData);
            // Signup finishes in the setup guide: company, test key, first calculation.
            navigate('/onboarding');
        } catch (err: any) {
            logger.error('Registration error:', err);
            setError(err.message || 'An error occurred during registration. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (userData: any) => {
        try {
            // Store the session and load the user; googleLogin navigates to /dashboard.
            await googleLogin(userData);
        } catch (error: any) {
            logger.error('Google sign-in error:', error);
            setError(error?.message || 'Google sign-in failed. Please try again.');
        }
    };

    const handleGoogleError = (error: string) => {
        logger.error('Google sign-in error:', error);
        setError(error);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {showGoogle && (
                <>
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Create your account
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No credit card. Your test API key is free and never billed.
                        </Typography>
                    </Box>

                    {/* Google Sign-In Section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom align="center">
                            Sign up with Google
                        </Typography>
                        <GoogleSignIn 
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            disabled={loading}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            or sign up with email
                        </Typography>
                    </Divider>
                </>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Full Name"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleChange}
                            error={!!validationErrors.full_name}
                            helperText={validationErrors.full_name}
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!validationErrors.email}
                            helperText={validationErrors.email}
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            error={!!validationErrors.password}
                            helperText={
                                validationErrors.password ||
                                'At least 8 characters, with upper and lower case, a number and a symbol.'
                            }
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            disabled={loading}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            error={!!validationErrors.confirmPassword}
                            helperText={validationErrors.confirmPassword}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            disabled={loading}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : 'Sign Up'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Already have an account?{' '}
                        <Link component={RouterLink} to="/login" variant="body2">
                            Sign in
                        </Link>
                    </Typography>
                </Box>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, textAlign: 'left' }}>
                    <Typography variant="subtitle2" gutterBottom>
                        What happens next
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 2.5, m: 0 }}>
                        <li>Add your company details and what you sell.</li>
                        <li>Create a test API key — one click, shown once.</li>
                        <li>Paste one request and see the calculation on your dashboard.</li>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0 }}>
                        About five minutes. No card, and no sales import needed to get a calculation back.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}; 